/**
 * Activation Analytics Event Contract
 *
 * Privacy-safe analytics taxonomy for first-value activation funnels.
 *
 * Primary responsibilities:
 * - Define activation event names and exact property types
 * - Normalize bounded labels used across activation surfaces
 * - Sanitize raw analytics payloads before server persistence
 *
 * Dependencies:
 * - None
 */

export const ActivationEventNames = {
  PUBLIC_CHAT_STARTER_PROMPT_CLICKED: 'public_chat_starter_prompt_clicked',
  CHAT_FIRST_USER_MESSAGE_SUBMITTED: 'chat_first_user_message_submitted',
  CHAT_FIRST_AI_RESPONSE_STARTED: 'chat_first_ai_response_started',
  CHAT_FIRST_AI_RESPONSE_SUCCEEDED: 'chat_first_ai_response_succeeded',
  CHAT_FIRST_AI_RESPONSE_FAILED: 'chat_first_ai_response_failed',
  CHAT_SECOND_USER_MESSAGE_SUBMITTED: 'chat_second_user_message_submitted',
  SAVE_CHAT_CTA_VIEWED: 'save_chat_cta_viewed',
  SAVE_CHAT_CTA_CLICKED: 'save_chat_cta_clicked',
  SIGNUP_COMPLETED_FROM_ACTIVATION: 'signup_completed_from_activation',
  CHAT_SHARE_CLICKED: 'chat_share_clicked',
  SHARED_CHAT_VIEWED: 'shared_chat_viewed',
  SHARED_CHAT_FOLLOWUP_SUBMITTED: 'shared_chat_followup_submitted'
} as const

export const ACTIVATION_EVENT_NAMES = Object.values(ActivationEventNames)

export type ActivationEventName = (typeof ACTIVATION_EVENT_NAMES)[number]

export const ACTIVATION_EVENT_NAME_SET: ReadonlySet<ActivationEventName> =
  new Set(ACTIVATION_EVENT_NAMES)

export const ACTIVATION_SURFACES = [
  'homepage',
  'public_chat',
  'authenticated_chat',
  'shared_chat',
  'sidekick',
  'space',
  'embed',
  'nexus',
  'unknown'
] as const

export type ActivationSurface = (typeof ACTIVATION_SURFACES)[number]

export const ACTIVATION_PROMPT_TYPES = [
  'starter',
  'trending',
  'suggested',
  'assistant_intro',
  'custom',
  'unknown'
] as const

export type ActivationPromptType = (typeof ACTIVATION_PROMPT_TYPES)[number]

export const ACTIVATION_MESSAGE_SOURCES = [
  'composer',
  'homepage_ask',
  'homepage_search',
  'url_query',
  'starter',
  'retry',
  'sidekick',
  'shared_followup',
  'unknown'
] as const

export type ActivationMessageSource = (typeof ACTIVATION_MESSAGE_SOURCES)[number]

export const ACTIVATION_SAVE_TRIGGERS = [
  'first_ai_response',
  'second_message',
  'public_header',
  'attachment_gate',
  'manual',
  'unknown'
] as const

export type ActivationSaveTrigger = (typeof ACTIVATION_SAVE_TRIGGERS)[number]

export const ACTIVATION_ERROR_TYPES = [
  'network',
  'rate_limit',
  'subscription_required',
  'validation',
  'abort',
  'unknown'
] as const

export type ActivationErrorType = (typeof ACTIVATION_ERROR_TYPES)[number]

export const ACTIVATION_FAILURE_STAGES = [
  'validation',
  'chat_create',
  'message_persist',
  'model_response',
  'public_session_save',
  'unknown'
] as const

export type ActivationFailureStage = (typeof ACTIVATION_FAILURE_STAGES)[number]

export const ACTIVATION_CHAT_TYPES = [
  'personal',
  'group',
  'public_snapshot',
  'public_session',
  'unknown'
] as const

export type ActivationChatType = (typeof ACTIVATION_CHAT_TYPES)[number]

export const ACTIVATION_REFERRER_TYPES = [
  'direct',
  'internal',
  'external',
  'search',
  'social',
  'unknown'
] as const

export type ActivationReferrerType = (typeof ACTIVATION_REFERRER_TYPES)[number]

export interface PublicChatStarterPromptClickedProperties {
  surface: ActivationSurface
  prompt_type: ActivationPromptType
  session_id: string
  is_anonymous: boolean
}

export interface ChatFirstUserMessageSubmittedProperties {
  surface: ActivationSurface
  is_anonymous: boolean
  source: ActivationMessageSource
  session_id: string
}

export interface ChatFirstAiResponseStartedProperties {
  surface: ActivationSurface
  is_anonymous: boolean
  session_id: string
}

export interface ChatFirstAiResponseSucceededProperties {
  surface: ActivationSurface
  is_anonymous: boolean
  latency_ms: number
  session_id: string
}

export interface ChatFirstAiResponseFailedProperties {
  surface: ActivationSurface
  is_anonymous: boolean
  error_type: ActivationErrorType
  stage: ActivationFailureStage
  session_id: string
}

export interface ChatSecondUserMessageSubmittedProperties {
  surface: ActivationSurface
  is_anonymous: boolean
  session_id: string
}

export interface SaveChatCtaViewedProperties {
  surface: ActivationSurface
  trigger: ActivationSaveTrigger
  is_anonymous: boolean
}

export interface SaveChatCtaClickedProperties {
  surface: ActivationSurface
  trigger: ActivationSaveTrigger
  is_anonymous: boolean
}

export interface SignupCompletedFromActivationProperties {
  surface: ActivationSurface
  source: ActivationMessageSource | 'save_chat_cta'
  had_first_ai_response: boolean
  had_second_message: boolean
}

export interface ChatShareClickedProperties {
  surface: ActivationSurface
  chat_type: ActivationChatType
}

export interface SharedChatViewedProperties {
  share_id_or_hash: string
  is_anonymous: boolean
  referrer_type: ActivationReferrerType
}

export interface SharedChatFollowupSubmittedProperties {
  is_anonymous: boolean
  share_id_or_hash: string
}

export interface ActivationEventPropertiesByName {
  [ActivationEventNames.PUBLIC_CHAT_STARTER_PROMPT_CLICKED]:
    PublicChatStarterPromptClickedProperties
  [ActivationEventNames.CHAT_FIRST_USER_MESSAGE_SUBMITTED]:
    ChatFirstUserMessageSubmittedProperties
  [ActivationEventNames.CHAT_FIRST_AI_RESPONSE_STARTED]:
    ChatFirstAiResponseStartedProperties
  [ActivationEventNames.CHAT_FIRST_AI_RESPONSE_SUCCEEDED]:
    ChatFirstAiResponseSucceededProperties
  [ActivationEventNames.CHAT_FIRST_AI_RESPONSE_FAILED]:
    ChatFirstAiResponseFailedProperties
  [ActivationEventNames.CHAT_SECOND_USER_MESSAGE_SUBMITTED]:
    ChatSecondUserMessageSubmittedProperties
  [ActivationEventNames.SAVE_CHAT_CTA_VIEWED]: SaveChatCtaViewedProperties
  [ActivationEventNames.SAVE_CHAT_CTA_CLICKED]: SaveChatCtaClickedProperties
  [ActivationEventNames.SIGNUP_COMPLETED_FROM_ACTIVATION]:
    SignupCompletedFromActivationProperties
  [ActivationEventNames.CHAT_SHARE_CLICKED]: ChatShareClickedProperties
  [ActivationEventNames.SHARED_CHAT_VIEWED]: SharedChatViewedProperties
  [ActivationEventNames.SHARED_CHAT_FOLLOWUP_SUBMITTED]:
    SharedChatFollowupSubmittedProperties
}

export type ActivationEventProperties =
  ActivationEventPropertiesByName[ActivationEventName]

export interface ActivationAnalyticsEvent<Name extends ActivationEventName> {
  event: Name
  properties: ActivationEventPropertiesByName[Name]
}

function isActivationEventName(value: string): value is ActivationEventName {
  return ACTIVATION_EVENT_NAME_SET.has(value as ActivationEventName)
}

function includesString<const Values extends readonly string[]>(
  values: Values,
  value: string
): value is Values[number] {
  return values.includes(value)
}

function readString(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function readSafeIdentifier(record: Record<string, unknown>, key: string) {
  const value = readString(record, key)
  if (!value) return null
  if (value.length > 256) return null
  if (value.includes('@')) return null

  return value
}

function readBoolean(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return null

  const normalized = value.trim().toLowerCase()
  if (normalized === 'true') return true
  if (normalized === 'false') return false

  return null
}

function readNonNegativeInteger(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null
  }

  return Math.round(value)
}

function readEnum<const Values extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: Values
): Values[number] | null {
  const value = readString(record, key)
  if (!value) return null

  return includesString(values, value) ? value : null
}

function sanitizeStarterPromptClicked(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const promptType = readEnum(record, 'prompt_type', ACTIVATION_PROMPT_TYPES)
  const sessionId = readSafeIdentifier(record, 'session_id')
  const isAnonymous = readBoolean(record, 'is_anonymous')

  if (!surface || !promptType || !sessionId || isAnonymous === null) return null

  return {
    surface,
    prompt_type: promptType,
    session_id: sessionId,
    is_anonymous: isAnonymous
  }
}

function sanitizeFirstUserMessageSubmitted(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const source = readEnum(record, 'source', ACTIVATION_MESSAGE_SOURCES)
  const sessionId = readSafeIdentifier(record, 'session_id')

  if (!surface || isAnonymous === null || !source || !sessionId) return null

  return {
    surface,
    is_anonymous: isAnonymous,
    source,
    session_id: sessionId
  }
}

function sanitizeFirstAiResponseStarted(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const sessionId = readSafeIdentifier(record, 'session_id')

  if (!surface || isAnonymous === null || !sessionId) return null

  return {
    surface,
    is_anonymous: isAnonymous,
    session_id: sessionId
  }
}

function sanitizeFirstAiResponseSucceeded(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const latencyMs = readNonNegativeInteger(record, 'latency_ms')
  const sessionId = readSafeIdentifier(record, 'session_id')

  if (!surface || isAnonymous === null || latencyMs === null || !sessionId) {
    return null
  }

  return {
    surface,
    is_anonymous: isAnonymous,
    latency_ms: latencyMs,
    session_id: sessionId
  }
}

function sanitizeFirstAiResponseFailed(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const errorType = readEnum(record, 'error_type', ACTIVATION_ERROR_TYPES)
  const stage = readEnum(record, 'stage', ACTIVATION_FAILURE_STAGES)
  const sessionId = readSafeIdentifier(record, 'session_id')

  if (!surface || isAnonymous === null || !errorType || !stage || !sessionId) {
    return null
  }

  return {
    surface,
    is_anonymous: isAnonymous,
    error_type: errorType,
    stage,
    session_id: sessionId
  }
}

function sanitizeSecondUserMessageSubmitted(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const sessionId = readSafeIdentifier(record, 'session_id')

  if (!surface || isAnonymous === null || !sessionId) return null

  return {
    surface,
    is_anonymous: isAnonymous,
    session_id: sessionId
  }
}

function sanitizeSaveChatCta(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const trigger = readEnum(record, 'trigger', ACTIVATION_SAVE_TRIGGERS)
  const isAnonymous = readBoolean(record, 'is_anonymous')

  if (!surface || !trigger || isAnonymous === null) return null

  return {
    surface,
    trigger,
    is_anonymous: isAnonymous
  }
}

function sanitizeSignupCompleted(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const source =
    readEnum(record, 'source', ACTIVATION_MESSAGE_SOURCES) ??
    (readString(record, 'source') === 'save_chat_cta' ? 'save_chat_cta' : null)
  const hadFirstAiResponse = readBoolean(record, 'had_first_ai_response')
  const hadSecondMessage = readBoolean(record, 'had_second_message')

  if (
    !surface ||
    !source ||
    hadFirstAiResponse === null ||
    hadSecondMessage === null
  ) {
    return null
  }

  return {
    surface,
    source,
    had_first_ai_response: hadFirstAiResponse,
    had_second_message: hadSecondMessage
  }
}

function sanitizeChatShareClicked(record: Record<string, unknown>) {
  const surface = readEnum(record, 'surface', ACTIVATION_SURFACES)
  const chatType = readEnum(record, 'chat_type', ACTIVATION_CHAT_TYPES)

  if (!surface || !chatType) return null

  return {
    surface,
    chat_type: chatType
  }
}

function sanitizeSharedChatViewed(record: Record<string, unknown>) {
  const shareIdOrHash = readSafeIdentifier(record, 'share_id_or_hash')
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const referrerType = readEnum(record, 'referrer_type', ACTIVATION_REFERRER_TYPES)

  if (!shareIdOrHash || isAnonymous === null || !referrerType) return null

  return {
    share_id_or_hash: shareIdOrHash,
    is_anonymous: isAnonymous,
    referrer_type: referrerType
  }
}

function sanitizeSharedChatFollowupSubmitted(record: Record<string, unknown>) {
  const isAnonymous = readBoolean(record, 'is_anonymous')
  const shareIdOrHash = readSafeIdentifier(record, 'share_id_or_hash')

  if (isAnonymous === null || !shareIdOrHash) return null

  return {
    is_anonymous: isAnonymous,
    share_id_or_hash: shareIdOrHash
  }
}

export function sanitizeActivationProperties<Name extends ActivationEventName>(
  eventName: Name,
  rawProperties: Record<string, unknown>
): ActivationEventPropertiesByName[Name] | null {
  if (!isActivationEventName(eventName)) return null

  const sanitized =
    eventName === ActivationEventNames.PUBLIC_CHAT_STARTER_PROMPT_CLICKED
      ? sanitizeStarterPromptClicked(rawProperties)
      : eventName === ActivationEventNames.CHAT_FIRST_USER_MESSAGE_SUBMITTED
        ? sanitizeFirstUserMessageSubmitted(rawProperties)
        : eventName === ActivationEventNames.CHAT_FIRST_AI_RESPONSE_STARTED
          ? sanitizeFirstAiResponseStarted(rawProperties)
          : eventName === ActivationEventNames.CHAT_FIRST_AI_RESPONSE_SUCCEEDED
            ? sanitizeFirstAiResponseSucceeded(rawProperties)
            : eventName === ActivationEventNames.CHAT_FIRST_AI_RESPONSE_FAILED
              ? sanitizeFirstAiResponseFailed(rawProperties)
              : eventName === ActivationEventNames.CHAT_SECOND_USER_MESSAGE_SUBMITTED
                ? sanitizeSecondUserMessageSubmitted(rawProperties)
                : eventName === ActivationEventNames.SAVE_CHAT_CTA_VIEWED ||
                    eventName === ActivationEventNames.SAVE_CHAT_CTA_CLICKED
                  ? sanitizeSaveChatCta(rawProperties)
                  : eventName ===
                      ActivationEventNames.SIGNUP_COMPLETED_FROM_ACTIVATION
                    ? sanitizeSignupCompleted(rawProperties)
                    : eventName === ActivationEventNames.CHAT_SHARE_CLICKED
                      ? sanitizeChatShareClicked(rawProperties)
                      : eventName === ActivationEventNames.SHARED_CHAT_VIEWED
                        ? sanitizeSharedChatViewed(rawProperties)
                        : sanitizeSharedChatFollowupSubmitted(rawProperties)

  return sanitized as ActivationEventPropertiesByName[Name] | null
}

export function buildActivationEvent<Name extends ActivationEventName>(
  eventName: Name,
  properties: ActivationEventPropertiesByName[Name]
): ActivationAnalyticsEvent<Name> {
  const sanitized = sanitizeActivationProperties(
    eventName,
    properties as Record<string, unknown>
  )

  if (!sanitized) {
    throw new Error(`Invalid activation analytics properties for ${eventName}`)
  }

  return {
    event: eventName,
    properties: sanitized
  }
}
